import { getStatusIcon } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/coach-partnership-status';
import type { CoachingPartnershipStatus } from '@efdigital/ll-addon-data';
import {
  type CoacheeListEntry,
  type CoacheePartnershipFields,
  type CoacheeSessionFields,
  summarizeCoacheeSessions,
} from '@efdigital/ll-xceed-v2/utils/coaching/coach-coachees';
import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';

interface CoacheeCardSignature {
  Args: {
    coachee: CoacheeListEntry<CoacheePartnershipFields, CoacheeSessionFields>;
  };
  Element: HTMLDivElement;
}

/**
 * "Chosen" is a settled state, so it reads as a solid fill; every other
 * status is still in progress and gets an outline instead. The fill uses
 * `text-bg-primary` so it follows the theme's accent in both colour modes
 * rather than pinning a colour here.
 */
function badgeClass(partnershipStatus: CoachingPartnershipStatus): string {
  return partnershipStatus === 'chosen'
    ? 'text-bg-primary'
    : 'border text-body';
}

/**
 * Compact next-session stamp, e.g. `Mon, 1 Sep, 09:00`.
 *
 * The addon's `formatDate` spells the weekday and month out in full, which is too
 * long to sit in a card beside the delivered count.
 */
function formatNextSession(startTime: string): string {
  return new Date(startTime).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function sessionCount(count: number): string {
  return count === 1 ? '1 session' : `${count} sessions`;
}

function attentionLabel(count: number): string {
  return count === 1
    ? '1 session needs your attention'
    : `${count} sessions need your attention`;
}

const CoacheeCard: TOC<CoacheeCardSignature> = <template>
  <div
    class="card border shadow-sm rounded-4 h-100 position-relative overflow-hidden"
    data-test-coachee-card={{@coachee.coacheeUserId}}
    ...attributes
  >
    <div class="card-body p-4 d-flex flex-column">
      {{#let (summarizeCoacheeSessions @coachee.sessions) as |summary|}}
        <div class="d-flex align-items-start gap-3 mb-3">
          <span
            class="ll-avatar-xs rounded-circle text-bg-primary d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-semibold fs-5"
            aria-hidden="true"
          >{{@coachee.initials}}</span>

          <div class="flex-grow-1 min-w-0">
            <h3 class="h5 fw-semibold mb-0 text-truncate">
              {{! `stretched-link` makes the whole card the target, so the
                  underline appears when the pointer is anywhere over it. }}
              <LinkTo
                @route="coaching.coach.coachees.coachee"
                @model={{@coachee.coacheeUserId}}
                class="stretched-link link-body-emphasis link-underline-opacity-0 link-underline-opacity-100-hover link-offset-2"
              >{{@coachee.displayName}}</LinkTo>
            </h3>

            {{#let
              (getStatusIcon @coachee.status @coachee.hasChemistrySession)
              as |statusDisplay|
            }}
              {{#if statusDisplay}}
                <span
                  class="badge rounded-pill fw-semibold px-3 py-2 d-inline-flex align-items-center gap-1 mt-2
                    {{badgeClass @coachee.status}}"
                >
                  <iconify-icon
                    inline
                    icon={{statusDisplay.icon}}
                    aria-hidden="true"
                  />
                  {{statusDisplay.label}}
                </span>
              {{/if}}
            {{/let}}
          </div>

          {{! Static cue that the card opens the coachee's page. }}
          <iconify-icon
            inline
            icon="ph:caret-right"
            class="text-body-tertiary fs-5 flex-shrink-0"
            aria-hidden="true"
          />
        </div>

        <dl class="d-flex gap-4 mb-0 mt-auto pt-3 border-top lh-sm min-w-0">
          <div class="flex-fill min-w-0">
            <dt class="small text-body-secondary fw-normal mb-1">Next session</dt>
            {{#if summary.nextSession}}
              <dd class="mb-0 fw-semibold text-break">{{formatNextSession
                  summary.nextSession.startTime
                }}</dd>
            {{else}}
              <dd class="mb-0 text-body-secondary">None booked</dd>
            {{/if}}
          </div>

          <div class="flex-fill min-w-0">
            <dt class="small text-body-secondary fw-normal mb-1">Delivered</dt>
            {{#if summary.deliveredCount}}
              <dd class="mb-0 fw-semibold">{{sessionCount
                  summary.deliveredCount
                }}</dd>
            {{else}}
              <dd class="mb-0 text-body-secondary">None yet</dd>
            {{/if}}
          </div>
        </dl>

        {{#if summary.actionRequired.length}}
          <div
            class="d-flex align-items-center gap-2 rounded-3 border border-warning border-2 px-3 py-2 mt-3 small fw-semibold text-break"
          >
            <iconify-icon
              inline
              icon="ph:warning"
              class="text-warning"
              aria-hidden="true"
            />
            {{attentionLabel summary.actionRequired.length}}
          </div>
        {{/if}}
      {{/let}}
    </div>
  </div>
</template>;

export default CoacheeCard;
